import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Header } from "@/components/Header";
import { AnalysisSequence } from "@/components/AnalysisSequence";
import { ResultsDashboard } from "@/components/ResultsDashboard";
import { useI18n } from "@/lib/i18n";
import { api, trackEvent } from "@/lib/api";
import { toast } from "sonner";

const INDUSTRIES = [
  "Restaurants & hospitality",
  "Legal services",
  "Medical & dental",
  "Real estate",
  "Home services",
  "Construction",
  "Retail / E-commerce",
  "B2B / SaaS",
  "Automotive",
  "Beauty & wellness",
  "Finance & insurance",
  "Education",
  "Other",
];

const OBJECTIVE_KEYS = ["leads", "calls", "sales", "appointments", "ads", "web", "unsure"];
const ADS_KEYS = ["yes", "no", "previously", "unknown"];

const StepShell = ({ children, title, subtitle }) => (
  <motion.div
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.4 }}
    className="max-w-3xl mx-auto"
  >
    <h2 className="font-display font-bold text-3xl md:text-4xl tracking-tight mb-3">{title}</h2>
    {subtitle && <p className="text-[color:var(--impact-ink-2)] text-lg mb-10">{subtitle}</p>}
    {children}
  </motion.div>
);

const Field = ({ label, children }) => (
  <label className="block mb-5">
    <div className="eyebrow mb-2">{label}</div>
    {children}
  </label>
);

const inputCls =
  "w-full px-4 py-3 rounded-lg border border-[color:var(--impact-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--impact-ink)]/20 focus:border-[color:var(--impact-ink)] transition-colors";

const RadioTile = ({ selected, onClick, children, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`text-left p-5 rounded-xl border transition-all duration-200 ${
      selected
        ? "border-[color:var(--impact-ink)] bg-[color:var(--impact-ink)] text-white shadow-sm"
        : "border-[color:var(--impact-border)] bg-white hover:border-[color:var(--impact-ink)]/50"
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <span className="font-medium">{children}</span>
      {selected && <CheckCircle2 size={18} />}
    </div>
  </button>
);

const AuditPage = () => {
  const { t, lang } = useI18n();
  const [step, setStep] = useState(1);
  const [phase, setPhase] = useState("form"); // form | analyzing | done
  const [audit, setAudit] = useState(null);

  const [form, setForm] = useState({
    company_name: "",
    website_url: "",
    industry: "",
    city: "",
    objective: "",
    ads_status: "",
    monthly_spend: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    consent: true,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 1) {
      if (!form.company_name || !form.website_url || !form.industry || !form.city) {
        toast.error(t("audit.required"));
        return false;
      }
      const urlRx = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[^\s]*)?$/i;
      if (!urlRx.test(form.website_url)) {
        toast.error(t("audit.invalidUrl"));
        return false;
      }
    }
    if (step === 2 && !form.objective) {
      toast.error(t("audit.required"));
      return false;
    }
    if (step === 3 && !form.ads_status) {
      toast.error(t("audit.required"));
      return false;
    }
    if (step === 4) {
      if (!form.first_name || !form.last_name || !form.email || !form.phone) {
        toast.error(t("audit.required"));
        return false;
      }
      const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRx.test(form.email)) {
        toast.error(t("audit.invalidEmail"));
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    trackEvent("audit_step_completed", { step });
    if (step < 4) setStep(step + 1);
    else submit();
  };

  const back = () => step > 1 && setStep(step - 1);

  const submit = async () => {
    setPhase("analyzing");
    trackEvent("audit_started", {});
    try {
      const payload = {
        company: {
          company_name: form.company_name,
          website_url: form.website_url,
          industry: form.industry,
          city: form.city,
        },
        objective: form.objective,
        ads: { status: form.ads_status, monthly_spend: form.monthly_spend || null },
        contact: {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          consent: form.consent,
        },
        language: lang,
      };
      const { data } = await api.post("/audits", payload);
      setAudit(data);
      trackEvent("audit_completed", { audit_id: data.id });
    } catch (e) {
      toast.error("Erreur d'analyse / Analysis error");
      setPhase("form");
    }
  };

  // Kick to results only once loader animation done AND audit ready
  const [loaderDone, setLoaderDone] = useState(false);
  React.useEffect(() => {
    if (loaderDone && audit) setPhase("done");
  }, [loaderDone, audit]);

  if (phase === "done" && audit) {
    return (
      <div className="min-h-screen">
        <Header />
        <ResultsDashboard audit={audit} />
      </div>
    );
  }

  if (phase === "analyzing") {
    return (
      <div className="min-h-screen">
        <Header />
        <AnalysisSequence active onDone={() => setLoaderDone(true)} />
      </div>
    );
  }

  const progressPct = ((step - 1) / 4) * 100;
  const stepLabels = ["company", "objectives", "ads", "contact"];

  return (
    <div className="min-h-screen" data-testid="audit-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-16">
        {/* Progress */}
        <div className="max-w-3xl mx-auto mb-10">
          <div className="flex items-center justify-between text-xs text-[color:var(--impact-ink-2)] mb-3">
            <span data-testid="audit-progress-label">
              {t("audit.progress")} {step} {t("audit.of")} 4 — {t(`audit.steps.${stepLabels[step - 1]}`)}
            </span>
            <span>{Math.round(((step - 1) / 4) * 100)}%</span>
          </div>
          <div className="h-1 bg-[color:var(--impact-border)] rounded-full overflow-hidden">
            <div className="h-full bg-[color:var(--impact-ink)] transition-[width] duration-500" style={{ width: `${progressPct + 25}%` }} />
          </div>
        </div>

        <div className="impact-card p-8 md:p-14">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <StepShell key="s1" title={t("audit.step1.title")} subtitle={t("audit.step1.subtitle")}>
                <Field label={t("audit.step1.companyName")}>
                  <input data-testid="input-company-name" className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
                </Field>
                <Field label={t("audit.step1.websiteUrl")}>
                  <input data-testid="input-website-url" className={inputCls} placeholder="https://" value={form.website_url} onChange={(e) => set("website_url", e.target.value)} />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={t("audit.step1.industry")}>
                    <select data-testid="input-industry" className={inputCls} value={form.industry} onChange={(e) => set("industry", e.target.value)}>
                      <option value="">{t("audit.step1.industryPlaceholder")}</option>
                      {INDUSTRIES.map((i) => (
                        <option key={i} value={i}>{i}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t("audit.step1.city")}>
                    <input data-testid="input-city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} />
                  </Field>
                </div>
              </StepShell>
            )}

            {step === 2 && (
              <StepShell key="s2" title={t("audit.step2.title")} subtitle={t("audit.step2.subtitle")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {OBJECTIVE_KEYS.map((k) => (
                    <RadioTile key={k} selected={form.objective === k} onClick={() => set("objective", k)} testId={`objective-${k}`}>
                      {t(`audit.step2.options.${k}`)}
                    </RadioTile>
                  ))}
                </div>
              </StepShell>
            )}

            {step === 3 && (
              <StepShell key="s3" title={t("audit.step3.title")} subtitle={t("audit.step3.subtitle")}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {ADS_KEYS.map((k) => (
                    <RadioTile key={k} selected={form.ads_status === k} onClick={() => set("ads_status", k)} testId={`ads-status-${k}`}>
                      {t(`audit.step3.options.${k}`)}
                    </RadioTile>
                  ))}
                </div>
                {form.ads_status === "yes" && (
                  <div className="mt-8">
                    <div className="eyebrow mb-3">{t("audit.step3.spendTitle")}</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {t("audit.step3.spend").map((s, i) => (
                        <RadioTile key={i} selected={form.monthly_spend === s} onClick={() => set("monthly_spend", s)} testId={`spend-${i}`}>
                          {s}
                        </RadioTile>
                      ))}
                    </div>
                  </div>
                )}
              </StepShell>
            )}

            {step === 4 && (
              <StepShell key="s4" title={t("audit.step4.title")} subtitle={t("audit.step4.subtitle")}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label={t("audit.step4.firstName")}>
                    <input data-testid="input-first-name" className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                  </Field>
                  <Field label={t("audit.step4.lastName")}>
                    <input data-testid="input-last-name" className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                  </Field>
                </div>
                <Field label={t("audit.step4.email")}>
                  <input data-testid="input-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
                </Field>
                <Field label={t("audit.step4.phone")}>
                  <input data-testid="input-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </Field>
                <label className="flex items-start gap-3 mt-4 text-sm text-[color:var(--impact-ink-2)]">
                  <input data-testid="input-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-1" />
                  <span>{t("audit.step4.consent")}</span>
                </label>
              </StepShell>
            )}
          </AnimatePresence>
        </div>

        {/* Nav */}
        <div className="max-w-3xl mx-auto flex items-center justify-between mt-8">
          <button
            onClick={back}
            data-testid="audit-back-btn"
            disabled={step === 1}
            className="btn-ghost disabled:opacity-40"
          >
            <ArrowLeft size={16} /> {t("audit.back")}
          </button>
          <button onClick={next} data-testid="audit-next-btn" className="btn-primary">
            {step < 4 ? t("audit.next") : t("audit.submit")} <ArrowRight size={16} />
          </button>
        </div>
      </main>
    </div>
  );
};

export default AuditPage;
